import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { dashboardApi } from '../services/api';

const Analytics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await dashboardApi.getStats();
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load stats:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const statusData = [
    { name: 'New', value: stats?.overview?.new_count || 0, color: '#9CA3AF' },
    { name: 'Contacted', value: stats?.overview?.contacted_count || 0, color: '#FBBF24' },
    { name: 'Replied', value: stats?.overview?.replied_count || 0, color: '#3B82F6' },
    { name: 'Partnered', value: stats?.overview?.partnered_count || 0, color: '#10B981' },
  ];

  return (
    <div>
      <h2 className="text-3xl font-bold text-gray-900 mb-8">Analytics & Insights</h2>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-sm font-medium">Avg Followers</p>
          <p className="text-3xl font-bold mt-2">
            {Math.round(stats?.overview?.avg_followers || 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-sm font-medium">Avg Engagement</p>
          <p className="text-3xl font-bold mt-2">
            {(Number(stats?.overview?.avg_engagement) || 0).toFixed(2)}%
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-sm font-medium">Avg Score</p>
          <p className="text-3xl font-bold mt-2">
            {Math.round(stats?.overview?.avg_score || 0)}/100
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-sm font-medium">Response Rate</p>
          <p className="text-3xl font-bold mt-2">
            {stats?.overview?.contacted_count > 0
              ? ((stats?.overview?.replied_count / stats?.overview?.contacted_count) * 100).toFixed(1)
              : 0}%
          </p>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Discovery Trend */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Discovery Trend (7 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats?.daily || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="discovery_date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="influencers_discovered" stroke="#3B82F6" strokeWidth={2} name="Discovered" />
              <Line type="monotone" dataKey="with_email" stroke="#10B981" strokeWidth={2} name="With Email" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Status Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Status Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Outreach Performance */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Outreach Performance (7 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats?.outreach || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="outreach_date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="total_sent" fill="#3B82F6" name="Sent" />
              <Bar dataKey="delivered_count" fill="#10B981" name="Delivered" />
              <Bar dataKey="replied_count" fill="#8B5CF6" name="Replied" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Reply Rate Trend */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Reply Rate Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats?.outreach || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="outreach_date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="reply_rate" stroke="#10B981" strokeWidth={2} name="Reply Rate (%)" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">Daily Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discovered
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  With Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Followers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Score
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(stats?.daily || []).map((day, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(day.discovery_date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {day.influencers_discovered}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {day.with_email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {Math.round(day.avg_followers || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {Math.round(day.avg_score || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
